import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import { ResponsiveBox, HStack, Typography } from "../primitives";
import ERPSBaseDatePicker from "../custom/inputs/ERPSDatePicker";
import ERPSBaseTextField from "../custom/inputs/ERPSBaseTextField";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import { useCustomHook } from "../custom/useCustomHook";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import { getAvailableDocumentsHeading, EMPTY_DOCUMENTS_CRITERIA } from "../../constants/AvailableDocumentsConstant";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import CardList from "../custom/containers/cardList";
import actions from "../../actions";
import ErrorMessage from "../custom/containers/ErrorMessage";
import {isBigFI, setFinancialInstitutionUIData} from "../../utils/helper";
import { RoleType, FI } from "../../constants/sharedConstants.js";
import {isBillerUserAndPortalType} from "../../utils/bpportal-common-utils";

const AvailableDocuments = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const apiExceptionRef = useRef();
  const billerIdFromPath = props?.billerInfo?.searchedBillerId || props?.location?.state?.searchedBillerId;
  const reqBillerId = props?.billerInfo?.billerId || props?.location?.state?.billerId;
  const billerType = props?.billerInfo?.billerType || props?.location?.state?.billerType;
  const [pageNumber, setPageNumber] = useState(0);
  const [rowPerPage, setRowPerPage] = useState(25);
  const loginUser = useSelector((state) => state.loginUserReducer?.user);
  const [documentData, setDocumentData] = useState(null);
  const [sourceList, setSourceList] = useState();
  const [allSources, setAllSources] = useState();
  const [showApiExceptionMessage, setShowApiExceptionMessage] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(null);
  const [isBillpayPortal] = useState(localStorage.getItem("type") === "billpay");
  const [isBillerRole] = useState(loginUser.role.role_type === RoleType.BILLER_ADMIN.type || loginUser.role.role_type === RoleType.BILLER_USER.type);
  const isBillerCrossReferenceProfile = props?.isBillerCrossReferenceProfile;

  const documentResult = useSelector( (state) => state.availableDocumentReducer.availableDocument );
  const [apiExceptionMessage, setApiExceptionMessage] = useState(null);
  const availableDocumentReducer = useSelector( (state) => state.availableDocumentReducer );

  const fiReducer = useSelector((state) =>
    isBillpayPortal || isBillerCrossReferenceProfile ? state.fiReducer : null
  );
  const [fiList, setFiList] = useState([]);
  const [fiMap, setFiMap] = useState(null);

  const [validationErrors, setValidationErrors] = useState([]);
  const [highlightError, setHighlightError] = useState({});
  const [showFiDropdown, setShowFiDropdown] = useState(false);
  const [showFiColumn, setShowFiColumn] = useState(false);


  useEffect(() => {
    if (showApiExceptionMessage)
      setApiExceptionMessage(availableDocumentReducer.error || availableDocumentReducer.downloadReportError || availableDocumentReducer.downloadReportListError || availableDocumentReducer?.downloadDocumentError);

    if (apiExceptionRef.current) {
      apiExceptionRef.current.focus();
    }
  }, [showApiExceptionMessage, validationErrors?.length, availableDocumentReducer?.downloadDocumentError]);


  useEffect(() => {
    if (filterLoading === false) {
      let isShowError = (availableDocumentReducer.error || availableDocumentReducer.downloadReportError
                        || availableDocumentReducer.downloadReportListError || availableDocumentReducer?.downloadDocumentError ? true : false);
      if (isShowError) {
        setApiExceptionMessage(availableDocumentReducer.error || availableDocumentReducer.downloadReportError || availableDocumentReducer.downloadReportListError);
        setShowApiExceptionMessage(true);
      }
    }
  }, [filterLoading]);

  useEffect(() => {
    (isBillpayPortal || isBillerCrossReferenceProfile) && setFinancialInstitutionUIData(fiReducer?.filteredFinancialInstitutionList, setFiMap, setFiList, "", loginUser?.languageCode);
  }, [fiReducer?.filteredFinancialInstitutionList, loginUser?.languageCode]);

  useEffect(() => {
    sourceListInit();
    if ((isBillpayPortal || isBillerCrossReferenceProfile) && !(fiReducer?.filteredFinancialInstitutionList?.length > 0)) {
      dispatch(actions.fiActions.getFilteredFinancialInstitutionList());
    }

    setShowFiDropdown((isBillpayPortal && isBillerRole) || isBillerCrossReferenceProfile);
  }, []);

  function sourceListInit() {
    let sourceValues = [
      { value: "Service Fee Invoice", label: t("erps.availabledocuments.sourcelist.servicefeeinvoice") },
      { value: "Bill Payment", label: t("erps.availabledocuments.sourcelist.billpayment") },
      { value: "Retail Bill Payment", label: t("erps.availabledocuments.sourcelist.retailbillpayment") },
      { value: "Corporate Bill Payment", label: t("erps.availabledocuments.sourcelist.corporatebillpayment") },
    ];
    if (loginUser.commonConfig?.IS_RETURNS_PORTAL_LIVE === "Y")
      sourceValues.push({ value: "Recalled and Rejected Payment Report", label: t("erps.availabledocuments.sourcelist.recalledandrejectedpaymentreport") });

    let skipValues;
    if (isBillerRole && isBillpayPortal || isBillerCrossReferenceProfile) {
      skipValues = ["Retail Bill Payment", "Corporate Bill Payment", "Service Fee Invoice"];
    } else if (loginUser.fiNumber) {
      if (loginUser.fiNumber === 99 || loginUser.fiNumber === 50)
        skipValues = ["Bill Payment"];
      else if (isBigFI(loginUser.fiNumber))
        skipValues = ["Retail Bill Payment", "Corporate Bill Payment"];
      else
        skipValues = ["Retail Bill Payment", "Corporate Bill Payment", "Service Fee Invoice"];
    }

    if (skipValues?.length) {
      sourceValues = sourceValues.filter((src) => !skipValues.includes(src.value));
    }

    setAllSources(sourceValues.map(source => source.value).join());

    sourceValues.unshift({ value: "", label: "" });
    setSourceList(sourceValues);
  }

  useEffect(() => {
    if (!allSources)  // not yet initialized
      return;

    const searchBillerFilesDto = {
      start_date: "",
      end_date: "",
      biller_id: props.billerId || reqBillerId,
      selected_fi_number: "0",
      start_off_set: 0,
      limit: 25,
      source: allSources,
      ...(props.centralizedBillerId != null && {ctb_id: props.centralizedBillerId})
    };
    setActualRequest(searchBillerFilesDto);

    setShowFiColumn(showFiDropdown);
    setFilterLoading(true);
    dispatch(
      actions.availableDocumentActions.getAvailableDocument( searchBillerFilesDto, setFilterLoading )
    );
  }, [allSources]);

  useEffect(() => {
    if (documentResult)
      setDocumentData( documentResult.data.map((data) => ({ ...data, id: data.file_id })) );
  }, [documentResult]);

  const errorRef = useRef();

  const getUpdatedReportPath = (reportPath) => {
    const pattern = /\\/;
    return pattern.test(reportPath.charAt(0))
      ? reportPath.slice(1)
      : reportPath;
  };

  const fileDownload = (billerFilesDto, fileFormat) => {
    setShowApiExceptionMessage(false);

    const requestStartTime = new Date(
      performance.timeOrigin + performance.now()
    ).toISOString();

    dispatch(
      actions.availableDocumentActions.downloadDocuments(
        {
          ...billerFilesDto,
          file_description: billerFilesDto?.file_type,
          ...(fileFormat === "PDF" && {
            pdf_link: getUpdatedReportPath(billerFilesDto?.pdf_link),
          }),
          ...(fileFormat === "CSV" && {
            csv_link: getUpdatedReportPath(billerFilesDto?.csv_link),
          }),
        },
        fileFormat,
        requestStartTime,
        {
          setShowApiExceptionMessage,
          searchBillerFilesDto: actualRequest,
          setInitialLoading,
        }
      )
    );
  };

  const todayDate = new Date();
  const minDateValue = new Date(
    todayDate.getFullYear(),
    todayDate.getMonth() - 13,
    todayDate.getDate()
  );
  const maxDateValue = new Date();

  const [values, setValues] = useState({
    billerId: loginUser?.searchBillerFilesDto?.biller_id,
    fiNumber: loginUser?.searchBillerFilesDto?.fi_number,
    billerName: loginUser?.searchBillerFilesDto?.biller_name,
    fiBillerWithBillerId: (loginUser.fiNumber == FI.CTB_BILLER && loginUser.billerCrossReferenceDto)
        ? loginUser.billerCrossReferenceDto.centralizedBillerId : loginUser?.searchBillerFilesDto?.fi_biller_with_biller_id,
  });

  const handleFilter = () => {
    setShowApiExceptionMessage(false);
    setApiExceptionMessage(null);
    setShowFiColumn(showFiDropdown);
    handleAvailableDocumentsSearch(allSources, showFiDropdown ? null : "0");
  };

  const {
    handleAvailableDocumentsSearch,
    searchCriteriaChangeHandler,
    resetAvailableDocumentsHandler,
    isSearchValid,
    paginateAvailableDocuments,
    searchCriteria,
    actualRequest,
    hasSearch,
    setActualRequest
  } = useCustomHook({
    tableResults: documentData,
    tableHeadings: getAvailableDocumentsHeading(fileDownload),
    initialStates: {
      ...EMPTY_DOCUMENTS_CRITERIA,
      ...(showFiDropdown && { selectedFiNumber: "0" }),
    },
    setErrorMessage: setValidationErrors,
    errorMessage: validationErrors,
    propsComp: {
      ...props,
      billerId: props?.billerId || reqBillerId || values?.billerId,
      centralizedBillerId: props?.centralizedBillerId,
    },
    setHighlightError,
    minDateValue,
    maxDateValue,
    setInitialLoading,
    setLoading: setFilterLoading,
    setPageNumber,
    setRowPerPage,
  });

  useEffect(() => {
    if (errorRef.current) {
      errorRef.current.focus();
    }
  }, [!isSearchValid]);

  // added so that on initial load if ALl fis is selected set it as 0 to avoid the error of empty criteria
  useEffect(() => {
    if (showFiDropdown && !searchCriteria.selectedFiNumber) {
      searchCriteriaChangeHandler("selectedFiNumber")({
        target: { value: "0" },
      });
    }
  }, [showFiDropdown]);

  const searchResultText = () => {
    let textVal;
    let count;
    if (documentResult !== null && documentResult !== undefined) {
      textVal = hasSearch ? t("erps.availabledocuments.filtereddocumentscodesresults", "Filtered documents")
                          : t("erps.availabledocuments.availabledocumentscodesresults", "All documents");
      count = documentResult.count;
    } else {
      textVal = t("erps.availabledocuments.label.searchresults", "Search Results");
    }
    return { textVal, count };
  };

  const getPageHeading = () => {
    if (billerType) {
      return billerType === "child"
        ? `${t("erps.availabledocuments.text.documentforbiller")}: ${billerIdFromPath}`
        : `${t("erps.availabledocuments.text.documentforparentbiller")}: ${billerIdFromPath}`;
    } else if (props?.billerId) {
      return t("erps.availabledocuments.page.title", "Available documents");
    }
    return t("erps.downloaddocuments.page.title");
  };

  const handleSourceChange = (event) => {
    searchCriteriaChangeHandler("source")(event);
    if (isBillpayPortal && isBillerRole)
      setShowFiDropdown(event.target.value !== 'Recalled and Rejected Payment Report' );
  };

  return (
    <div role="main">
      <ResponsiveBox ml={3}>
        {(billerType || props?.pageName === "biller-profile") && (
          <Breadcrumb
            {...props}
            updateSetState={props?.updateSetState}
            pathValue={
              billerType
                ? "/downloads-documents/view-documents"
                : "/manage-billers/user-profile"
            }
            componentAccess={props?.parentComponentAccess}
          />
        )}

        {((apiExceptionMessage && showApiExceptionMessage) ||
          Boolean(validationErrors.length)) && (
          <ResponsiveBox className="manageUsers__errorMsg__container" role="alert" mt="2">
            <ErrorMessage
              error={apiExceptionMessage || validationErrors}
              errorRef={apiExceptionRef}
            />
          </ResponsiveBox>
        )}
        {isBillerUserAndPortalType(loginUser, "billpay") &&
            loginUser.commonConfig?.IS_RETURNS_PORTAL_LIVE === "Y" && (
            <ResponsiveBox mt={2}>
              <Typography fontWeight={"bold"}>
                {t("erps.welcome.page.important.notice")}
              </Typography>
              <Typography variant="body1">
                {t("erps.welcome.page.important.notice.description",
                )}
              </Typography>
            </ResponsiveBox>)}
        <ResponsiveBox mt={4}>
          <Typography variant="h1">{getPageHeading()}</Typography>
        </ResponsiveBox>
        {!props?.billerId && !billerType && (
          <ResponsiveBox className="manageUsers__wrap__box">
            <Typography variant="body1">
              {t("erps.availabledocuments.page.description", "Download reports and invoices")}
            </Typography>
          </ResponsiveBox>
        )}
        {!props?.billerId && !billerType && props.pageName !== "biller-profile" && (
            <ResponsiveBox mt={3} sx={{ display: "flex" }}>
              <Typography className="available__documents__value">
                {t("erps.availabledocuments.label.billerId", "Biller ID")}{" "}
                <br />{" "}
                <label className="test-label">
                  {values.fiBillerWithBillerId}
                </label>
              </Typography>
              <Typography className="available__documents__value">
                {t("erps.availabledocuments.label.description", "Biller name")}{" "}
                <br /> <label className="test-label">{values.billerName}</label>
              </Typography>
            </ResponsiveBox>
          )}
        <ResponsiveBox mt={!billerType ? 7 : 4}>
          <Typography variant="h2">
            {t("erps.availabledocuments.filteravailabledocuments.heading", "Filter criteria")}
          </Typography>
        </ResponsiveBox>

        {isSearchValid ? null : (
          <ResponsiveBox
            className="manageUsers__errorMsg__container"
            mt={2}
            role="alert"
          >
            <ErrorMessage
              error={t("erps.manageusers.searchuser.error.emptyinput", "At least one search criteria must be provided.")}
              errorRef={errorRef}
            />
          </ResponsiveBox>
        )}
        <ResponsiveBox>
          <HStack spacing={1}>
            <ERPSBaseTextField
                id="source-container"
                type="text"
                select={true}
                label="Source"
                size="small"
                items={sourceList}
                onChange={handleSourceChange}
                value={searchCriteria.source}
                SelectProps={{native: true}}
              />
              <ResponsiveBox pt={2}>
                <ERPSBaseDatePicker
                  label={t("erps.availabledocuments.page.startdate", "Start date")}
                  onChange={searchCriteriaChangeHandler("startDate")}
                  value={searchCriteria.startDate || null}
                  error={highlightError?.startDate}
                  minDate={minDateValue}
                  maxDate={todayDate}
                  id={"avilable-document-start-date"}
                />
              </ResponsiveBox>
              <ResponsiveBox pt={2}>
                <ERPSBaseDatePicker
                  label={t("erps.availabledocuments.page.enddate", "End date")}
                  value={searchCriteria.endDate || null}
                  onChange={searchCriteriaChangeHandler("endDate")}
                  error={highlightError?.endDate}
                  minDate={minDateValue}
                  maxDate={todayDate}
                  id={"avilable-document-end-date"}
                />
              </ResponsiveBox>
            {showFiDropdown && (
                <ERPSBaseTextField id="selectedFiNumber"
                  value={searchCriteria.selectedFiNumber || ""}
                  type="text"
                  select={true}
                  label={t("erps.common.label.financialInstitution", "Financial Institution")}
                  onChange={searchCriteriaChangeHandler("selectedFiNumber")}
                  size="small"
                  items={fiList}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                />
            )}
          </HStack>
        </ResponsiveBox>
        <ResponsiveBox mt={-4} display="flex">
          <ERPSBaseButton
            variant="secondary"
            className="button_with_margin48"
            label={t("erps.common.button.reset", "Reset")}
            onClick={() => resetAvailableDocumentsHandler(allSources)}
          />
          <ResponsiveBox pl={2}>
            <ERPSBaseButton
              variant="primary"
              className="button_with_margin48"
              label={t("erps.common.button.filter", "Filter")}
              onClick={handleFilter}
              loading={filterLoading}
            />
          </ResponsiveBox>
        </ResponsiveBox>
        <ResponsiveBox mt={6}>
          <Typography variant="h2"> {searchResultText()?.textVal}</Typography>
        </ResponsiveBox>
        {searchResultText()?.count !== undefined && (
          <ResponsiveBox marginTop={1} marginBottom={1.5} sx={{ display: "flex" }}>
            <Typography variant="body1">
              {searchResultText()?.count}{" "}
              {t("erps.availabledocuments.availabledocuments.records", "records")}
            </Typography>
          </ResponsiveBox>
        )}
        {documentData !== null && documentData !== undefined && documentData.length > 0 && (
            <>
              <ResponsiveBox className="manageUsers_card_visible">
                <CardList
                  data={{
                    rows: documentData,
                    count: documentResult?.count,
                    columns: getAvailableDocumentsHeading(fileDownload, showFiColumn ? fiMap : null),
                  }}
                  onAnyPaginationAction={paginateAvailableDocuments}
                  pageNumber={pageNumber}
                  rowPerPage={rowPerPage}
                />
              </ResponsiveBox>

              <ResponsiveBox className="manageUsers_table_visible">
                <ResponsiveDataGrid
                  data={{
                    rows: documentData,
                    count: documentResult?.count,
                    columns: getAvailableDocumentsHeading(fileDownload, showFiColumn ? fiMap : null),
                  }}
                  onAnyPaginationAction={paginateAvailableDocuments}
                  sumOfColumnWidth="true"
                  tableLabelText={t(
                    "erps.downloaddocuments.page.title",
                    "Download documents"
                  )}
                  initialLoading={initialLoading}
                  pageNumber={pageNumber}
                  rowPerPage={rowPerPage}
                />
              </ResponsiveBox>
            </>
          )}
        {(documentResult === null ||
          searchResultText()?.count === undefined) && (
          <ResponsiveBox mt={1}>
            <Typography variant="body1">
              {" "}
              {t("erps.availabledocuments.searchuser.notstarted", "Search not started")}
            </Typography>
          </ResponsiveBox>
        )}
        {billerType && (
          <ERPSBaseButton
            variant="primary"
            className="button_with_margin48"
            label={t("erps.newuser.button.back")}
            onClick={() => {
              if (props?.checkRedirect) {
                props?.handleCloseViewDocument();
              } else {
                props.history.push("/downloads-documents");
              }
            }}
          />
        )}
      </ResponsiveBox>
    </div>
  );
};

export default withSidebar(AvailableDocuments);
